import { useMemo } from 'react'

import { useGetUserQuery } from 'common'
import {
  useGetPurchaseFulfillmentsByEventUuidsQuery,
} from 'shared/api/commerce/graphql_api'

export type PackageNameMap = Record<string, string>

/**
 * Fetches purchase fulfillment data for the given event UUIDs and returns
 * a lookup map from event UUID to package name.
 *
 * Each fulfillment's `fulfilledItemUuid` corresponds to the event UUID,
 * and `package.name` provides the display name.
 *
 * Skips the query when there are no UUIDs or no authenticated user.
 */
export const usePackageNameMap = (eventUuids: string[]): PackageNameMap => {
  const { data: currentUser } = useGetUserQuery()
  const ownerUuid = currentUser?.uuid

  const { data } = useGetPurchaseFulfillmentsByEventUuidsQuery(
    { eventUuids, ownerUuid: ownerUuid! },
    { skip: eventUuids.length === 0 || !ownerUuid }
  )

  return useMemo(() => {
    const map: PackageNameMap = {}
    const collection = data?.purchaseFulfillmentsByEventUuids?.collection
    if (!collection?.length) return map

    for (const fulfillment of collection) {
      const packageName = fulfillment.package?.name
      if (packageName && fulfillment.fulfilledItemUuid) {
        map[fulfillment.fulfilledItemUuid] = packageName
      }
    }

    return map
  }, [data])
}
